import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { ArrowRightIcon } from '@motorway/mw-highway-code';
import { Hyperlink } from '@motorway/mw-highway-code/alpha';
import { BUTTON_AS_TYPE, SIZE, VARIANT } from '@motorway/mw-highway-code/enums';

import LocalTexts from '../../../../HomepageTexts.json';
import { NEWS_AND_TOOLS_TILES } from '../../NewsAndTools.helper';

import styles from './NewsAndToolsCard.scss';

const LocalT = new MDText(LocalTexts);

export type NewsAndToolsCardProps = {
  img?: {
    alt: string;
    isBackgroundImage?: boolean;
    size: {
      height: `${number}%` | number;
      width: `${number}%` | number;
    },
    src: {
      avif: string;
      png: string;
      webp: string;
    }
  };
  isSectionVisible: boolean;
  label: string;
  link: string;
  onClick: () => void;
  openInNewTab?: boolean;
  readonly type: typeof NEWS_AND_TOOLS_TILES[keyof typeof NEWS_AND_TOOLS_TILES];
};

type NewsAndToolsContentWrapperProps = {
  children: React.ReactNode;
  className: string;
  rel: string;
  target: string;
}

const getTexts = (type: string) => ({
  cta: LocalT.translate(`NewsAndTools.${type}.cta`),
  title: LocalT.translate(`NewsAndTools.${type}.title`),
});

const isExternalLink = (r: string) => /^https?:\/\//.test(r);

const NewsAndToolsContentWrapper = ({
  children,
  className,
  link,
  onClick,
  rel,
  target,
}: Pick<NewsAndToolsCardProps, 'link' | 'onClick'> & NewsAndToolsContentWrapperProps) => (<>
  {
    isExternalLink(link)
      ? (
        <a
          {...{ className, onClick, rel, target }}
          href={link}
        >
          {children}
        </a>
      ) : (
        <Link
          {...{ className, onClick, rel, target }}
          to={link}
        >
          {children}
        </Link>
      )
  }
</>);

export const NewsAndToolsCard = forwardRef<HTMLDivElement, NewsAndToolsCardProps>(
  ({ img, isSectionVisible, link, onClick, openInNewTab, type }, ref) => {
    const { cta, title } = getTexts(type);

    return (
      <div ref={ref} data-testid='newsAndToolsCard'>
        <NewsAndToolsContentWrapper
          className={cx(styles.component, styles[type], {
            [styles.hasBackgroundImage]: img?.isBackgroundImage,
            [styles.loadBackgroundImage]: isSectionVisible,
          })}
          rel='noreferrer noopener'
          target={openInNewTab ? '_blank' : '_self'}
          {...{ link, onClick, openInNewTab }}
        >
          {
            img && (
              <picture className={cx({
                [styles.inlineImage]: !img?.isBackgroundImage,
              })}>
                <source srcSet={img?.src.avif} type="image/avif" />
                <source srcSet={img?.src.webp} type="image/webp" />
                <img
                  alt={img?.alt}
                  height={img?.size.height}
                  loading='lazy'
                  src={img?.src.png}
                  width={img?.size.width}
                />
              </picture>
            )
          }
          <div className={styles.content}>
            <h4 className={styles.title}>{title}</h4>
            <div className={styles.hyperlink}>
              <Hyperlink as={BUTTON_AS_TYPE.BUTTON} label={cta} size={SIZE.MEDIUM} variant={VARIANT.SECONDARY} />
              <ArrowRightIcon size={SIZE.XSMALL} />
            </div>
          </div>
        </NewsAndToolsContentWrapper>
      </div>
    );
  },
);
